import { validatePathParams } from '@/utils/hooks/api/safePathParams';
import {
  buildApiEndpoint,
  extractBody,
  extractError,
  extractErrorData,
} from '@/utils/hooks/api/typescript/methodHelpers';
import { parseErrorMessage } from '@/utils/hooks/api/vercel/helpers/parseErrorMessage';
import { useSnackBarContext } from '@/utils/snackbar';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useEffect } from 'react';
import { useMutation, UseMutationResult } from '@tanstack/react-query';

interface UsePutConfig {
  successMessage?: string;
  suppressSuccessMessage?: boolean;
  errorMessage?: string;
  errorMessageFallback?: string;
  errorMessagePrefix?: string;
  pathParams?: Record<string, string | number | undefined>;
  pathParamValidators?: Record<string, RegExp>;
}

export default function usePut<TData = unknown, TVariables = unknown>(
  endpoint: string,
  config: UsePutConfig = {}
): {
  request: UseMutationResult<AxiosResponse<TData>, AxiosError, TVariables>;
  fire: (data: TVariables) => void;
  fireAsync: (data: TVariables) => Promise<AxiosResponse<TData>>;
  isLoading: boolean;
  isError: boolean;
  data: TData | undefined;
  error: unknown;
  errorData: Record<string, unknown> | undefined;
} {
  let baseEndpoint = endpoint;
  let pathParamsValid = true;

  if (config.pathParams) {
    const { valid, path } = validatePathParams(
      endpoint,
      config.pathParams,
      config.pathParamValidators
    );
    pathParamsValid = valid;
    if (valid && path) {
      baseEndpoint = path;
    }
  }

  const fullEndpoint = buildApiEndpoint(baseEndpoint);
  const { updateSnack } = useSnackBarContext();

  const mutation = useMutation<AxiosResponse<TData>, AxiosError, TVariables>(
    (data) => {
      if (!pathParamsValid) {
        return Promise.reject(
          new Error('Invalid path parameters prevented request')
        );
      }
      return axios.put(fullEndpoint, data);
    }
  );

  // handle snack for error
  useEffect(() => {
    if (!mutation.isError) return;
    console.error('Error in usePut: ', mutation.error);

    const snackMessage = parseErrorMessage(config, mutation);

    updateSnack({
      severity: 'error',
      message: snackMessage,
    });
  }, [mutation.isError]);

  // handle snack for success
  useEffect(() => {
    if (!mutation.data || config?.suppressSuccessMessage) return;
    let message = config?.successMessage || '';
    if (
      mutation.data &&
      mutation.data.data &&
      typeof mutation.data.data === 'object' &&
      'message' in mutation.data.data
    ) {
      message =
        String((mutation.data.data as Record<string, unknown>).message) ||
        message;
    }
    updateSnack({
      severity: 'success',
      message,
    });
  }, [mutation.data, config?.successMessage, config?.suppressSuccessMessage]);

  const responseData: TData | undefined = extractBody<TData>(mutation.data);

  return {
    request: mutation,
    fire: mutation.mutate,
    fireAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    data: responseData,
    error: extractError(mutation.error ?? undefined),
    errorData: extractErrorData(mutation.error ?? undefined),
  };
}
